import { colors } from 'baseui/tokens';
import { string2money } from 'common/utils/string';
import Text from 'components/elements/text';
import { SeparatorHorizontal, SeparatorVertical } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import { CruiseContainer, CruiseContent, Container } from './styles';
import {
  CruiseSaleCruiseSaleDetailsFormType,
  CruiseSaleCruiseSaleGuestFormType,
} from '../form-type';

interface Props {
  data: CruiseSaleCruiseSaleDetailsFormType[];
  guests: CruiseSaleCruiseSaleGuestFormType[];
  isGrouped?: boolean;
}

export default function SummaryItem(props: Props) {
  const { t } = useTranslation();
  const { data: _data, guests, isGrouped } = props;

  const data = React.useMemo(() => {
    const temp: {
      name: string;
      basicFare: number;
      portCharge: number;
      tax: number;
      tipping: number;
      default: {
        basicFare: number;
        portCharge: number;
        tax: number;
        tipping: number;
      };
    } = {
      basicFare: 0,
      name: '',
      portCharge: 0,
      tax: 0,
      tipping: 0,
      default: {
        basicFare: _data[0].basicFare,
        portCharge: _data[0].portCharge,
        tax: _data[0].tax,
        tipping: _data[0].tipping,
      },
    };
    if (isGrouped) {
      const mergeName = guests.map((val) => val.guestName).join('\n');
      temp.name = `${mergeName} - ${_data[0].cruiseRoomName}`;
      temp.basicFare += _data[0].basicFare;
      temp.portCharge += _data[0].portCharge;
      temp.tax += _data[0].tax;
      temp.tipping += _data[0].tipping;
    } else {
      temp.name = `${_data[0].guestName} - ${_data[0].cruiseRoomName}`;
      _data?.forEach((val) => {
        temp.basicFare += val.basicFare;
        temp.portCharge += val.portCharge;
        temp.tax += val.tax;
        temp.tipping += val.tipping;
      });
    }

    return temp;
  }, [_data, guests, isGrouped]);

  return (
    <Container>
      <>
        {isGrouped ? (
          guests.map((guest, idx) => (
            <React.Fragment key={idx}>
              <Text variant="LabelSmall">
                {guest.guestName} - {_data[0].cruiseRoomName}
              </Text>
              <SeparatorVertical $margin={16} />
            </React.Fragment>
          ))
        ) : (
          <>
            <Text variant="LabelSmall">{data.name}</Text>
            <SeparatorVertical $margin={8} />
          </>
        )}
        <CruiseContainer>
          <CruiseContent>
            <Text variant="LabelSmall">{t('ticketSales:basic_fare')}</Text>
            {isGrouped && (
              <>
                <SeparatorHorizontal $margin={8} />
                <Text variant="LabelSmall" $style={{ color: colors.gray500 }}>
                  {`@ Rp ${string2money(data.default.basicFare || 0)} `}
                </Text>
              </>
            )}
          </CruiseContent>
          <Text variant="LabelSmall">
            {`Rp ${string2money(data.basicFare || 0)}`}
          </Text>
        </CruiseContainer>
        <CruiseContainer>
          <CruiseContent>
            <Text variant="LabelSmall">{t('ticketSales:port_charge')}</Text>
            {isGrouped && (
              <>
                <SeparatorHorizontal $margin={8} />
                <Text variant="LabelSmall" $style={{ color: colors.gray500 }}>
                  {`@ Rp ${string2money(data.default.portCharge || 0)}`}
                </Text>
              </>
            )}
          </CruiseContent>
          <Text variant="LabelSmall">
            {`Rp ${string2money(data.portCharge || 0)}`}
          </Text>
        </CruiseContainer>
        <CruiseContainer>
          <CruiseContent>
            <Text variant="LabelSmall">{t('ticketSales:tax')}</Text>
            {isGrouped && (
              <>
                <SeparatorHorizontal $margin={8} />
                <Text variant="LabelSmall" $style={{ color: colors.gray500 }}>
                  {`@ Rp ${string2money(data.default.tax || 0)}`}
                </Text>
              </>
            )}
          </CruiseContent>
          <Text variant="LabelSmall">
            {`Rp ${string2money(data.tax || 0)}`}
          </Text>
        </CruiseContainer>
        <CruiseContainer>
          <CruiseContent>
            <Text variant="LabelSmall">{t('ticketSales:tipping')}</Text>
            {isGrouped && (
              <>
                <SeparatorHorizontal $margin={8} />
                <Text variant="LabelSmall" $style={{ color: colors.gray500 }}>
                  {`@ Rp ${string2money(data.default.tipping || 0)}`}
                </Text>
              </>
            )}
          </CruiseContent>
          <Text variant="LabelSmall">
            {`Rp ${string2money(data.tipping || 0)}`}
          </Text>
        </CruiseContainer>
      </>
    </Container>
  );
}
